/**
 * <accepted-types-calendar> — the ticket types this entrance admits, read as
 * the event's own programme: a leading whole-run column, one column per
 * programme day, today's marked, each head a tri-state toggle over that day's
 * types and each row a toggle over one type.
 *
 * It borrows <programme-calendar>'s band grammar and imports that surface's
 * own three layout constants, so the two bands cannot drift a column width
 * apart. What it does NOT borrow is the row model: the console's row demands
 * price, capacity, sold and revenue — an accounting surface a door phone has
 * none of and partly may not read — which is why this is a sibling rather
 * than a `selectable` mode on that file.
 *
 * A COLUMN RENDERS ITS MEMBERS, NOT ITS FILING — the deliberate inversion of
 * the reference. A Fri–Sun pass appears in Friday's, Saturday's and Sunday's
 * columns: one id, one selection, the span token explaining the recurrence.
 * The reference files a multi-day pass under its first day alone because
 * repeating it would triple every accounting roll-up its column heads carry;
 * this surface has no roll-up, and a head whose tri-state acts over that day's
 * MEMBERS while its column lists only the types filed there would contradict
 * its own rows on screen. Each repeated instance carries a day-qualified
 * accessible name so a reader — and a harness driving by accessible name —
 * can address one column's instance unambiguously. The reference also keys a
 * drag protocol one zone per column; this band has no reorder, so that
 * conflict is inert here rather than resolved.
 *
 * THE ROW IS A CHIP, WITHOUT THE CHIPS CONTAINER. The library carries no
 * multi-select row: multi-selection exists as a pill, a dropdown listbox and a
 * data table, and <schmancy-list-item>'s `selected` emits `aria-current="true"`
 * for every role — right for its own consumers, wrong for a set where nine
 * rows are chosen at once. <schmancy-chips> is not the wrapper either: it is a
 * form field, so nine columns would be nine form fields holding one logical
 * selection, and its axis is a hardcoded wrap that inside a `w-72` column
 * reproduces the ragged wrap this surface exists to kill. <schmancy-filter-chip>
 * extends SchmancyElement and stands alone, announcing `aria-pressed` on a real
 * button; the view owns the union across columns. Its host classes forward onto
 * that button and trail last, which is what lets a chip sit flush on the band's
 * own surface rather than as a pill floating on it.
 *
 * THE BAND SPEAKS ITS OWN DOMAIN WORD AND SWALLOWS EVERY OTHER. Each control
 * inside it — filter chip, day box, master switch — emits schmancy's generic
 * `change`, bubbling and composed, so a band that emitted `change` too would
 * hand its host four indistinguishable events per gesture: three carrying an
 * inner control's own detail shape. The band emits `acceptedTypes` instead,
 * and stops `change` at its own root so no ancestor, present or future, ever
 * receives one.
 *
 * ONE ACCESSIBLE NAME PER CHIP. Name-from-content sums every unhidden node
 * inside the chip's button, so the visible name, the day-qualified string and
 * the span token together announced the name three ways in one breath. The
 * visible tokens are the decoration and are hidden; the one visually-hidden
 * string is the name.
 *
 * ALL TICKET TYPES IS A SWITCH, NOT ANOTHER CHIP. A chip is a member of a
 * selection set and would invite "all types is one more thing I can tick
 * alongside Saturday"; a switch is the band's own master. Flipped on by hand,
 * every row and day/whole-run head checks for real — the same `selected` a
 * chip-by-chip tick would have produced — so the operator reads the same
 * admitted set the door itself will read, not a dimmed promise of it. Only the
 * untouched default (a door that has never been narrowed) stays empty and
 * renders inert and dimmed in place rather than collapsing, since there the
 * band has nothing explicit to show yet. `inert` sits on each column and never
 * on the scroller, so panning the band with a thumb is untouched by it.
 *
 * THE MASTER SAYS WHAT THE SET DOES; THE DIM SAYS WHETHER YOU ARE EDITING IT.
 * Two readings of one empty selection, and only one of them is a claim about
 * admission: `[]` admits every type whether the master produced it or a hand
 * unticked its way down to it, so the switch reads on either way — a switch
 * reading off over an empty set would contradict the door's own enforcement and
 * the scan chrome's "all ticket types" at the same time. What the hand's own
 * clear does buy is the band staying open under the thumb that cleared it,
 * rather than dimming away the moment the last chip goes out.
 *
 * SHOWN, NEVER TOUCHED IS A THIRD STATE, AND EVERY CONTROL IN IT IS READONLY.
 * `readonly` is for a caller whose set is decided somewhere other than in this
 * band — a door deriving its own lane from the day — and in that state the band
 * is precisely what an operator has to read to understand the control deciding
 * it. `inert` would hand that reading to sighted operators and to nobody else:
 * an inert subtree is not exposed to accessibility APIs at all, and the HTML
 * standard's own reason for that names this band exactly — an inert subtree is
 * to hold no content critical to understanding the parts of the page that are
 * not inert, and this band IS the set the live control above it produces.
 * `disabled` is the lesser answer to the same reading: it drops the tab stop
 * and announces the control as disabled. So all four controls — the master
 * switch, the two head boxes, every row chip — take `readonly`, which each of
 * those primitives gates activation on while keeping its tab stop, its own
 * state and the rows' `aria-pressed`, and which leaves all four at full
 * strength where `disabled` would put 0.38 on the very set the operator is
 * here to read. `#emit`'s early return is what refuses the write, because a
 * `change` event dispatched straight at a control inside reaches its handler
 * however that control is painted; the `readonly` each control carries is what
 * refuses the activation itself, so a press never reaches a dispatch at all. A
 * switch that has flipped ITSELF under a set the door does not enforce is a
 * readout lying — a state this band cannot reach in either mode, for the
 * reason the master's own paragraph below gives.
 *
 * THE MASTER IS THE DOOR'S, BECAUSE ONLY THE DOOR HAS NOWHERE ELSE TO SAY IT.
 * A gate is swung between everything and one lane mid-shift and back, and this
 * switch is the whole of what tells its operator that an unnarrowed door
 * admits the lot. A band standing under a sentence that already says so is
 * offered no second way to hear it: the switch would restate the copy above it.
 * Flipped off by hand it clears the set outright rather than guessing at a
 * narrowing to return to — the operator who turned every row on by hand gets
 * the same clear as the operator who used the switch. What the switch does NOT
 * have is a resting off position over an empty set: clearing everything is what
 * admits everything, so the switch answers on again and the band opens
 * underneath for the next chip. Off is a gesture here, not a state.
 *
 * WHICH IS WHY THE MASTER IS `controlled`. `masterOn` is DERIVED, and the off
 * gesture's own result recomputes it to the `true` it already had: clearing the
 * set is what admits everything. lit-html commits nothing for an unchanged
 * binding, so an uncontrolled switch would keep the off it wrote itself on
 * activation and read against the very door it reports on. Controlled, it
 * writes nothing of its own and only says what was asked, which leaves the
 * binding the one thing that ever moves it.
 *
 * A PAST DAY DIMS ITS TEMPORAL HALF AND IS NEVER DISABLED — a late door
 * running past midnight still selects yesterday's lane.
 */
// Side-effect: registers <schmancy-scroll> and <schmancy-scroll-rail>.
import "@mhmo91/schmancy/layout";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { type Event, TicketType } from "@momo/types";
import {
	css,
	html,
	nothing,
	type PropertyValues,
	type TemplateResult,
} from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	COLUMN,
	FLAT_PANE,
	LIST_AIR,
} from "../../events/programme-calendar.view.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";

/** One ticket type as the door's band renders it. No price, no capacity, no
 *  sold figure: a door decides admission, and a number it cannot read is a
 *  number it must not print. */
export type AcceptedTypeRow = {
	readonly id: string;
	readonly name: string;
	/** The type's EFFECTIVE window (TicketType.resolveEntryWindow) in epoch
	 *  millis, and the zone it reads in — the day and the span derive from these
	 *  two and from nothing else. */
	readonly entryWindow: { readonly opensAt: number; readonly closesAt: number };
	readonly timezone: string;
};

/** One day column: the day's own dimension plus every row that ADMITS on it. */
type DayGroup = TicketType.ProgrammeDay & {
	readonly rows: readonly AcceptedTypeRow[];
};

@schmancyElement("accepted-types-calendar")
export class AcceptedTypesCalendarView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	/** Every ticket type this event holds; the band splits day-bound from
	 *  whole-run itself, so no caller answers that question a second time. */
	@property({ attribute: false }) types: readonly AcceptedTypeRow[] = [];

	/** The event the windows are read against — a window spanning the whole
	 *  schedule is tied to no day (TicketType.coversWholeEvent). */
	@property({ attribute: false }) event: Pick<Event.Event, "schedule"> | null =
		null;

	/** The ids this door admits. Empty is the unrestricted state the master
	 *  switch shows as on — the same polarity every reader of the filter uses. */
	@property({ attribute: false }) selected: readonly string[] = [];

	/** The instant "today" is read against, for a mount rendering a fixed
	 *  catalog at a chosen point in its run. Unset is the wall clock. */
	@property({ attribute: false }) now: number | null = null;

	/** What an EMPTY selection MEANS — a fact about the caller's set, not a
	 *  preference. A door that narrowed nothing admits everything, and the
	 *  master switch is that reading's control; a voucher's `unlocks` reveals
	 *  exactly what it names, so the switch is absent there — one reading "all
	 *  ticket types" while ON would say the opposite of what the set does.
	 *  Every read tests for "none", never for "all", because this is an
	 *  attribute whose runtime domain is any string and a typo at one call site
	 *  must not silently flip a door to admitting nothing. */
	@property({ type: String }) emptyMeans: "all" | "none" = "all";

	/** True where the copy beside this band already names what an empty
	 *  selection covers, so the band carries no master of its own. A switch
	 *  whose ON state is the very empty set that sentence calls "all of them"
	 *  says nothing the operator was not just told, and its OFF position has
	 *  no narrowing to return to but one it would have to invent. A door
	 *  names that reading nowhere but on this switch, which is why the master
	 *  is what a band carries by default: no door writes this attribute, and
	 *  one misspelt at another call site leaves every door's master standing. */
	@property({ type: Boolean }) emptyNamedBeside = false;

	/** Shown, never touched — the band as a readout of a set decided somewhere
	 *  other than here, and still the thing an operator reads to understand the
	 *  control that decides it. `#emit`'s early return is what refuses the
	 *  write, reached however the gesture arrives; the `readonly` this forwards
	 *  to every control is what stops each one answering a press with a state of
	 *  its own. All four keep their tab stop, their full strength and the rows'
	 *  `aria-pressed`, because a readout nobody can read is not one. Default
	 *  false, so a caller that never writes it reads exactly as it did. */
	@property({ type: Boolean }) readonly = false;

	/** Scrolled once per day set: re-scrolling would drag the band back under
	 *  the operator's own thumb every time anything above it changed. */
	#scrolledFor = "";

	/** True when `selected` reads empty because something deliberately cleared
	 *  it — a manual chip/day-head toggle, or the master switch turned off —
	 *  distinct from the empty a fresh event starts with. It governs the dim
	 *  alone: an operator mid-edit keeps the band open, and what that same empty
	 *  set ADMITS is not a question a gesture can change. Written before
	 *  `#emit` refuses, so a read-only gesture can still set it — harmless
	 *  because every change of mode resets it below, and it reaches nothing but
	 *  the dim either way. */
	#deselectedManually = false;

	#emit(value: readonly string[]): void {
		if (this.readonly) return;
		this.dispatchEvent(
			new CustomEvent("acceptedTypes", {
				detail: { value },
				bubbles: true,
				composed: true,
			}),
		);
	}

	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed);
		// A mount is reused across events AND across the two readings of the
		// same band (settings.view.ts binds .event/.types/.selected/.readonly as
		// plain properties on one instance), so a manual clear made inside one
		// editable set must not bleed into the next one. Both of these end that
		// set: a new event replaces the catalog it was made against, and going
		// read-only replaces the operator's own set with one derived elsewhere.
		if (changed.has("event") || changed.has("readonly")) {
			this.#deselectedManually = false;
		}
	}

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		const today = this.renderRoot.querySelector("[data-today]");
		const key = today?.getAttribute("data-today") ?? "";
		if (key === "" || this.#scrolledFor === key) return;
		const scroller = this.renderRoot.querySelector("schmancy-scroll");
		if (!(today instanceof HTMLElement) || !(scroller instanceof HTMLElement))
			return;
		this.#scrolledFor = key;
		// The band's own scroller is nudged directly rather than through
		// scrollIntoView, which walks every scrollable ancestor: this element
		// mounts inside an overlay sheet that scrolls vertically, and a band
		// still below the fold on first open would drag that sheet down to meet
		// it before the operator had read anything above it.
		scroller.scrollLeft +=
			today.getBoundingClientRect().left -
			scroller.getBoundingClientRect().left;
	}

	/** One type's row. `dayLabel` is the column's own full date, and it is the
	 *  chip's whole accessible name so a pass repeated across three columns is
	 *  three distinguishable controls rather than three identically-named ones.
	 *  Null outside a day column — the flat pane, the single-day card and the
	 *  whole-run column each hold one instance of a type, so the bare name is
	 *  already unambiguous there. */
	private row(row: AcceptedTypeRow, dayLabel: string | null): TemplateResult {
		const days = TicketType.daysCovered(row.entryWindow, row.timezone);
		const short = (day: string) =>
			new Date(`${day}T00:00:00.000Z`).toLocaleDateString(this.locale, {
				day: "numeric",
				month: "numeric",
				timeZone: "UTC",
			});
		return html`
			<schmancy-filter-chip
				class="w-full justify-between rounded-lg border-none"
				style="--filter-chip-selected-bg: var(--schmancy-sys-color-primary-container); --filter-chip-selected-color: var(--schmancy-sys-color-primary-onContainer); --filter-chip-selected-border: transparent;"
				value=${row.id}
				?readonly=${this.readonly}
				?selected=${this.selected.includes(row.id)}
				@change=${(e: CustomEvent<{ value: string; selected: boolean }>) => {
					const value = e.detail.selected
						? [...this.selected, e.detail.value]
						: this.selected.filter((id) => id !== e.detail.value);
					this.#deselectedManually = value.length === 0;
					this.#emit(value);
				}}
			>
				<schmancy-typography
					type="body"
					token="md"
					class="min-w-0 text-left"
					aria-hidden="true"
				>
					${row.name}
				</schmancy-typography>
				<schmancy-visually-hidden>
					${
						dayLabel === null
							? row.name
							: this.t("scanner.settings.typeOnDay", {
									name: row.name,
									day: dayLabel,
								})
					}
				</schmancy-visually-hidden>
				${when(
					days.length > 1,
					() => html`
						<schmancy-typography
							slot="trailing"
							type="label"
							token="xs"
							class="shrink-0 tabular-nums text-surface-onVariant"
							aria-hidden="true"
						>
							<schmancy-icon class="align-middle text-sm">date_range</schmancy-icon>
							${short(days[0] ?? "")}–${short(days.at(-1) ?? "")}
						</schmancy-typography>
					`,
				)}
			</schmancy-filter-chip>
		`;
	}

	/** The day's head — the reference's own opening, with the door-hours line
	 *  cut and the rail carrying this day's count and its tri-state box. The
	 *  RAIL sets the head's height, not the token, so dropping that line
	 *  genuinely shortens the head rather than freeing space inside it.
	 *
	 *  The head's control sits at the trailing rail and every row's at the
	 *  leading slot, so the two tiers never read as peers — the checkbox has no
	 *  size of its own, and position is what carries the subordination. */
	private dayHeading(group: DayGroup, today: string): TemplateResult {
		const isToday = group.day === today;
		const past = group.day < today;
		const calendar = new Date(`${group.day}T00:00:00.000Z`);
		const datePart = (opts: Intl.DateTimeFormatOptions) =>
			calendar.toLocaleDateString(this.locale, { ...opts, timeZone: "UTC" });
		const full = datePart({ weekday: "long", day: "numeric", month: "long" });
		const chosen = group.rows.filter((r) => this.selected.includes(r.id));
		const all = chosen.length === group.rows.length && group.rows.length > 0;
		return html`
			<schmancy-surface
				type=${isToday ? "container" : "containerLow"}
				class="block rounded-none"
			>
				<div class="flex items-center gap-2.5 px-3 pt-2.5 pb-3">
					<span
						class=${this.classMap({
							"grid size-9 shrink-0 place-items-center rounded-full": true,
							"bg-primary-default": isToday,
							"bg-surface-container": !isToday,
						})}
						aria-hidden="true"
					>
						<schmancy-typography
							type="title"
							token="lg"
							class=${this.classMap({
								"tabular-nums": true,
								"text-primary-on": isToday,
								"text-surface-onVariant": past,
							})}
						>
							${datePart({ day: "2-digit" })}
						</schmancy-typography>
					</span>
					<span
						class=${this.classMap({
							"flex flex-col": true,
							"text-surface-onVariant": past,
						})}
					>
						<!-- Weekday and month are two fragments of one date; read out one
						     by one they are not a date, so the whole date is carried here
						     instead and the fragments are decoration. -->
						<schmancy-visually-hidden>
							${full}${isToday ? `, ${this.t("admin.events.console.todayLabel")}` : ""}
						</schmancy-visually-hidden>
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class="tracking-widest"
							aria-hidden="true"
						>
							${datePart({ weekday: "short" })}
						</schmancy-typography>
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class="tracking-widest"
							aria-hidden="true"
						>
							${datePart({ month: "short" })}
						</schmancy-typography>
					</span>
					<!-- The count reads in the head's own chrome register — uppercase,
					     wide-tracked, label scale — because a head that spoke a row's
					     register would read as the list's first line rather than its lid. -->
					<schmancy-typography
						type="label"
						token="sm"
						transform="uppercase"
						class="ml-auto shrink-0 tabular-nums tracking-widest text-surface-onVariant"
						aria-hidden="true"
					>
						${chosen.length} / ${group.rows.length}
					</schmancy-typography>
					<schmancy-checkbox
						class="-mr-1"
						aria-label=${this.t("scanner.settings.allTypesOnDay", { day: full })}
						?readonly=${this.readonly}
						.value=${all}
						?indeterminate=${chosen.length > 0 && !all}
						@change=${(e: CustomEvent<{ value: boolean }>) => {
							const ids = group.rows.map((r) => r.id);
							const value = e.detail.value
								? [
										...this.selected,
										...ids.filter((id) => !this.selected.includes(id)),
									]
								: this.selected.filter((id) => !ids.includes(id));
							this.#deselectedManually = value.length === 0;
							this.#emit(value);
						}}
					></schmancy-checkbox>
				</div>
			</schmancy-surface>
		`;
	}

	override render(): TemplateResult | typeof nothing {
		const event = this.event;
		if (event === null || this.types.length === 0) return nothing;
		const dayed = this.types.filter(
			(r) => !TicketType.coversWholeEvent(r.entryWindow, event),
		);
		const wholeEvent = this.types.filter((r) =>
			TicketType.coversWholeEvent(r.entryWindow, event),
		);
		// MEMBERSHIP, not filing: a row lands in every column whose day its
		// window admits on, which is the same reading the head's own tri-state
		// acts over and the same one the door's default set is derived by.
		const byDay = new Map<string, AcceptedTypeRow[]>();
		for (const row of dayed) {
			for (const day of TicketType.daysCovered(row.entryWindow, row.timezone)) {
				const bucket = byDay.get(day);
				if (bucket) bucket.push(row);
				else byDay.set(day, [row]);
			}
		}
		const groups: readonly DayGroup[] = TicketType.programmeDaysOf(
			dayed.map((r) => ({ ...r.entryWindow, timezone: r.timezone })),
		).flatMap((day) => {
			const rows = byDay.get(day.day);
			return rows ? [{ ...day, rows }] : [];
		});
		const today = TicketType.dayKeyOf(
			this.now ?? Date.now(),
			dayed[0]?.timezone ?? this.types[0]?.timezone ?? "UTC",
		);
		// The band's own master over the whole set, and with it the dimming and
		// the inert columns that are its display. It stands only where this band
		// is the one thing naming the unnarrowed reading: a set whose empty means
		// "none" has no such reading to name, and a caller that names it in its
		// own copy leaves the switch nothing to add. The head row's own template
		// comment stays exactly as it stands: a comment inside a template is a
		// node in the shadow root, so rewording one repaints every door.
		const mastered = this.emptyMeans !== "none" && !this.emptyNamedBeside;
		// What the SET does, which is the only thing the master is entitled to
		// say: an empty selection admits every type, however it was arrived at.
		// A hand that unticked its way down to nothing did not narrow the door
		// to nothing — `isTicketAccepted` reads the same `[]` the master gesture
		// produces — so a master reading off there would contradict both the
		// door and the scan chrome's own "all ticket types" on the one polarity
		// this file exists to get right.
		const admitsEverything = mastered && this.selected.length === 0;
		const masterOn =
			admitsEverything ||
			(this.types.length > 0 &&
				this.types.every((t) => this.selected.includes(t.id)));
		// Whether the operator is mid-edit inside that same set, which is the
		// DIM's question and not the master's: a chip just cleared leaves the
		// band open for the next one to go in, rather than dimming under the
		// thumb that cleared it.
		const unrestricted = admitsEverything && !this.#deselectedManually;
		const wholeRunChosen = wholeEvent.filter((r) =>
			this.selected.includes(r.id),
		);

		return html`
			<div
				class="flex flex-col gap-3"
				@change=${(e: Event) => e.stopPropagation()}
			>
				<!-- The master head row: the whole-run head's own anatomy at full
				     width, because the state it carries is the band's and not one
				     column's. Absent where empty means "none": such a set has no
				     "all" state to be in, so the switch has nothing true to say. -->
				${when(
					mastered,
					() => html`
				<schmancy-surface type="containerLow" class="block rounded-xl">
					<div class="flex items-center gap-2.5 px-3 pt-2.5 pb-3">
						<span
							class="grid size-9 shrink-0 place-items-center rounded-full bg-surface-container"
							aria-hidden="true"
						>
							<schmancy-icon class="text-primary-default">done_all</schmancy-icon>
						</span>
						<schmancy-typography
							type="label"
							token="md"
							transform="uppercase"
							class="min-w-0 flex-1 truncate tracking-widest"
							aria-hidden="true"
						>
							${this.t("scanner.scan.door.allTypes")}
						</schmancy-typography>
						<schmancy-switch
							aria-label=${this.t("scanner.scan.door.allTypes")}
							controlled
							?readonly=${this.readonly}
							?checked=${masterOn}
							@change=${(e: CustomEvent<{ value: boolean }>) => {
								this.#deselectedManually = !e.detail.value;
								this.#emit(e.detail.value ? this.types.map((r) => r.id) : []);
							}}
						></schmancy-switch>
					</div>
				</schmancy-surface>
				`,
				)}

				<!-- Dimmed in place rather than collapsed: the operator still reads
				     what turning the switch back off would restore them to. -->
				<div
					class=${this.classMap({
						"transition-opacity": true,
						"opacity-40": unrestricted,
					})}
				>
					${
						groups.length === 0
							? html`
								<schmancy-surface
									type="containerLowest"
									opacity="glass"
									elevated
									class=${FLAT_PANE}
									?inert=${unrestricted}
								>
									<div class=${this.classMap({ "flex flex-col gap-2 px-2": true, [LIST_AIR]: true })} role="group">
										${repeat(
											this.types,
											(r) => r.id,
											(r) => this.row(r, null),
										)}
									</div>
								</schmancy-surface>
							`
							: groups.length === 1 && groups[0] && wholeEvent.length === 0
								? html`
									<schmancy-surface
										type="containerLowest"
										opacity="glass"
										elevated
										class=${FLAT_PANE}
										?inert=${unrestricted}
									>
										${this.dayHeading(groups[0], today)}
										<div class=${this.classMap({ "flex flex-col gap-2 px-2": true, [LIST_AIR]: true })} role="group">
											${repeat(
												groups[0].rows,
												(r) => r.id,
												(r) => this.row(r, null),
											)}
										</div>
									</schmancy-surface>
								`
								: html`
									<schmancy-scroll-rail>
										<schmancy-scroll direction="horizontal" hide>
											<div class="flex w-max items-start gap-3">
												${when(
													wholeEvent.length > 0,
													() => html`
														<schmancy-surface
															type="containerLowest"
															opacity="glass"
															elevated
															class=${COLUMN}
															?inert=${unrestricted}
														>
															<schmancy-surface type="containerLow" class="block rounded-none">
																<div class="flex items-center gap-2.5 px-3 pt-2.5 pb-3">
																	<span
																		class="grid size-9 shrink-0 place-items-center rounded-full bg-surface-container"
																		aria-hidden="true"
																	>
																		<schmancy-icon class="text-primary-default">event</schmancy-icon>
																	</span>
																	<schmancy-typography
																		type="label"
																		token="md"
																		transform="uppercase"
																		class="min-w-0 flex-1 line-clamp-2 tracking-widest"
																		aria-hidden="true"
																	>
																		${this.t("storefront.typeSelector.wholeEventTitle")}
																	</schmancy-typography>
																	<schmancy-typography
																		type="label"
																		token="sm"
																		transform="uppercase"
																		class="shrink-0 tabular-nums tracking-widest text-surface-onVariant"
																		aria-hidden="true"
																	>
																		${wholeRunChosen.length} / ${wholeEvent.length}
																	</schmancy-typography>
																	<schmancy-checkbox
																		class="-mr-1"
																		aria-label=${this.t("scanner.settings.allTypesWholeRun")}
																		?readonly=${this.readonly}
																		.value=${wholeRunChosen.length === wholeEvent.length}
																		?indeterminate=${wholeRunChosen.length > 0 && wholeRunChosen.length < wholeEvent.length}
																		@change=${(
																			e: CustomEvent<{ value: boolean }>,
																		) => {
																			const ids = wholeEvent.map((r) => r.id);
																			const value = e.detail.value
																				? [
																						...this.selected,
																						...ids.filter(
																							(id) =>
																								!this.selected.includes(id),
																						),
																					]
																				: this.selected.filter(
																						(id) => !ids.includes(id),
																					);
																			this.#deselectedManually =
																				value.length === 0;
																			this.#emit(value);
																		}}
																	></schmancy-checkbox>
																</div>
															</schmancy-surface>
															<div
																class=${this.classMap({ "flex flex-col gap-2 px-2": true, [LIST_AIR]: true })}
																role="group"
																aria-label=${this.t("storefront.typeSelector.wholeEventTitle")}
															>
																${repeat(
																	wholeEvent,
																	(r) => r.id,
																	(r) => this.row(r, null),
																)}
															</div>
														</schmancy-surface>
													`,
												)}
												${repeat(
													groups,
													(g) => g.day,
													(g) => html`
														<schmancy-surface
															type="containerLowest"
															opacity="glass"
															elevated
															class=${COLUMN}
															?inert=${unrestricted}
															data-today=${g.day === today ? g.day : nothing}
															aria-current=${ifDefined(
																g.day === today ? "date" : undefined,
															)}
														>
															${this.dayHeading(g, today)}
															<div
																class=${this.classMap({ "flex flex-col gap-2 px-2": true, [LIST_AIR]: true })}
																role="group"
																aria-label=${new Date(
																	`${g.day}T00:00:00.000Z`,
																).toLocaleDateString(this.locale, {
																	weekday: "long",
																	day: "numeric",
																	month: "long",
																	timeZone: "UTC",
																})}
															>
																${repeat(
																	g.rows,
																	(r) => r.id,
																	(r) =>
																		this.row(
																			r,
																			new Date(
																				`${g.day}T00:00:00.000Z`,
																			).toLocaleDateString(this.locale, {
																				weekday: "long",
																				day: "numeric",
																				month: "long",
																				timeZone: "UTC",
																			}),
																		),
																)}
															</div>
														</schmancy-surface>
													`,
												)}
											</div>
										</schmancy-scroll>
									</schmancy-scroll-rail>
								`
					}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"accepted-types-calendar": AcceptedTypesCalendarView;
	}
}
